import styled, { css, keyframes } from 'styled-components';
import { brandTheme } from '../../../theme/brandTokens';
import { darkPastel } from '../../../theme/pastelPill';

/**
 * Contacts — mirrors routes/contacts.index.tsx in the Remix reference.
 * Colours from --hd-* tokens; radii follow the reference scale (2xl = 22px).
 */

const FG = 'var(--hd-foreground)';
const MUTED_FG = 'var(--hd-muted-foreground)';
const MUTED = 'var(--hd-muted)';
const BORDER = 'var(--hd-border)';
const CARD = 'var(--hd-card)';
const BG = 'var(--hd-background)';
/* Matches version3: table head is py-3.5 (14px) + h-8 sort (32px). */
const TABLE_HEAD_HEIGHT = '60px';

const MUTED_30 = 'color-mix(in srgb, var(--hd-muted) 30%, transparent)';
const MUTED_40 = 'color-mix(in srgb, var(--hd-muted) 40%, transparent)';
const MUTED_50 = 'color-mix(in srgb, var(--hd-muted) 50%, transparent)';
const FG_20 = 'color-mix(in srgb, var(--hd-foreground) 20%, transparent)';
const FG_05 = 'color-mix(in srgb, var(--hd-foreground) 5%, transparent)';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

/* version3's popover: rounded-2xl border bg-card p-1.5 shadow-lg, fading up. */
const popover = css`
  position: absolute;
  z-index: 40;
  margin-top: 6px;
  overflow-y: auto;
  border: 1px solid ${BORDER};
  border-radius: 22px;
  background: ${CARD};
  box-shadow:
    0 10px 15px -3px rgba(15, 23, 42, 0.1),
    0 4px 6px -4px rgba(15, 23, 42, 0.1);
  animation: ${fadeUp} 0.2s ease-out both;
`;

export const StyledContacts = styled.div`
  ${brandTheme}
  min-height: 100%;
  background: ${BG};
  color: ${FG};

  .contacts-main {
    width: 100%;
    max-width: 1480px;
    margin: 0 auto;
    padding: 32px;

    @media (max-width: 767px) {
      padding: 24px 16px;
    }
  }

  .contacts-header {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-bottom: 16px;
  }

  .header-top {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;

    @media (min-width: 1024px) {
      flex-direction: row;
      align-items: flex-end;
    }
  }

  .header-copy {
    flex: 1;
    min-width: 0;
  }

  .header-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 4px 12px;
    border: 1px solid ${BORDER};
    border-radius: 999px;
    font-size: 12px;
    font-weight: 500;
    color: ${MUTED_FG};
    background: ${CARD};
    box-shadow: var(--hd-shadow-card);
  }

  .page-title {
    margin: 0;
    font-size: 24px;
    font-weight: 600;
    line-height: 32px;
    letter-spacing: -0.025em;
    text-transform: none;
    color: ${FG};
  }

  .page-subtitle {
    margin: 4px 0 0;
    font-size: 14px;
    line-height: 20px;
    color: ${MUTED_FG};
  }

  .header-actions {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 8px;
  }

  .btn-outline {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 36px;
    padding: 0 12px;
    border: 1px solid ${BORDER};
    border-radius: 14px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 500;
    color: ${MUTED_FG};
    background: ${BG};
    cursor: pointer;
    transition: color 0.15s ease;

    &:hover {
      color: ${FG};
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    &.active {
      color: var(--hd-primary);
      border-color: color-mix(in oklab, var(--hd-primary) 20%, transparent);
      background: color-mix(in oklab, var(--hd-primary) 5%, transparent);
    }
  }

  .btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 36px;
    padding: 0 14px;
    border: none;
    border-radius: 18px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    color: #fff;
    background: var(--hd-gradient-houdiny);
    box-shadow: var(--hd-shadow-card);
    cursor: pointer;
    transition: transform 0.15s ease;

    &:hover {
      transform: scale(1.02);
    }
  }

  .network-grid {
    display: grid;
    gap: 12px;

    @media (min-width: 640px) {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }

  .network-card {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 16px;
    border: 1px solid ${BORDER};
    border-radius: 18px;
    background: ${BG};
    text-align: left;
    cursor: default;
  }

  .network-card-top {
    display: flex;
    width: 100%;
    align-items: center;
    gap: 8px;
  }

  .network-card-icon {
    display: flex;
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    align-items: center;
    justify-content: center;
    border-radius: 14px;
    color: var(--hd-primary);
    background: ${MUTED};
  }

  .network-card-label {
    display: inline-flex;
    min-width: 0;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 500;
    line-height: 20px;
    color: ${FG};
  }

  .network-card-count {
    margin-left: auto;
    font-size: 20px;
    font-weight: 600;
    line-height: 28px;
    font-variant-numeric: tabular-nums;
    color: ${FG};
  }

  .network-card-hint {
    margin: 8px 0 0;
    font-size: 12px;
    line-height: 16px;
    color: ${MUTED_FG};
  }

  .network-card-action {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: 12px;
    font-size: 12px;
    font-weight: 500;
    color: var(--hd-primary);
  }

  .card-soon {
    display: inline-block;
    padding: 2px 7px;
    border-radius: 999px;
    font-size: 8.5px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    white-space: nowrap;
    color: ${MUTED_FG};
    background: ${MUTED};
  }

  /* KPI grid */
  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;

    @media (min-width: 768px) {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    @media (min-width: 1024px) {
      grid-template-columns: repeat(6, minmax(0, 1fr));
    }
  }

  /* Source breakdown */
  .source-card {
    overflow: hidden;
    padding: 20px;
    border: 1px solid ${BORDER};
    border-radius: 22px;
    background: ${CARD};
    box-shadow: var(--hd-shadow-card);
  }

  .source-card-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .source-card-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: ${MUTED_FG};
  }

  .source-card-hint {
    font-size: 11px;
    color: ${MUTED_FG};
  }

  .source-bar {
    display: flex;
    height: 8px;
    margin-top: 16px;
    overflow: hidden;
    border-radius: 999px;
    background: ${MUTED};

    & + .source-bar {
      margin-top: 10px;
      height: 6px;
    }
  }

  .source-bar-segment {
    height: 100%;
  }

  .source-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 24px;
    margin-top: 16px;
  }

  .source-legend-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
  }

  .source-legend-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }

  .source-legend-label {
    font-weight: 500;
    color: ${FG};
  }

  .source-legend-count {
    color: ${MUTED_FG};
    font-variant-numeric: tabular-nums;
  }

  /* Filter bar */
  .filter-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    padding: 8px;
    border: 1px solid ${BORDER};
    border-radius: 22px;
    background: ${CARD};
    box-shadow: var(--hd-shadow-card);
  }

  /* version3's filter Select: a pill that opens a popover of options. */
  .fselect {
    position: relative;
  }

  .fselect-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 36px;
    padding: 0 10px 0 12px;
    border: 1px solid ${BORDER};
    border-radius: 999px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
    color: ${FG};
    background: ${BG};
    cursor: pointer;
    transition:
      background 0.15s ease,
      box-shadow 0.15s ease;

    > svg:first-child {
      width: 14px;
      height: 14px;
      color: ${MUTED_FG};
    }

    .chev {
      width: 14px;
      height: 14px;
      color: ${MUTED_FG};
      transition: transform 0.15s ease;
    }

    &:hover {
      background: ${MUTED_40};
    }

    &.open {
      box-shadow: 0 0 0 2px ${FG_05};

      .chev {
        transform: rotate(180deg);
      }
    }

    &.active {
      color: var(--hd-primary);
      border-color: color-mix(in oklab, var(--hd-primary) 20%, transparent);
      background: color-mix(in oklab, var(--hd-primary) 5%, transparent);

      > svg:first-child,
      .chev {
        color: var(--hd-primary);
      }
    }

    .fselect-label {
      max-width: 200px;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .fselect-menu {
    ${popover}
    left: 0;
    width: 224px;
    max-height: 288px;
    padding: 6px;
  }

  .menu-backdrop {
    position: fixed;
    inset: 0;
    z-index: 30;
    padding: 0;
    border: none;
    background: transparent;
    cursor: default;
  }

  .fselect-option {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 8px 10px;
    border: none;
    border-radius: 18px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 500;
    text-align: left;
    color: ${MUTED_FG};
    background: transparent;
    cursor: pointer;
    transition:
      background 0.15s ease,
      color 0.15s ease;

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    svg {
      flex-shrink: 0;
      width: 14px;
      height: 14px;
      color: var(--hd-primary);
    }

    &:hover {
      color: ${FG};
      background: ${MUTED_40};
    }

    &.selected {
      color: ${FG};
      background: ${MUTED};
    }
  }

  .filter-right {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
  }

  .search-wrap {
    position: relative;
  }

  .search-icon {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: ${MUTED_FG};
    pointer-events: none;
  }

  .search-input {
    width: 320px;
    height: 36px;
    padding: 0 12px 0 32px;
    border: 1px solid ${BORDER};
    border-radius: 14px;
    font-family: inherit;
    font-size: 14px;
    color: ${FG};
    background: ${BG};
    transition:
      border-color 0.15s ease,
      box-shadow 0.15s ease;

    &::placeholder {
      color: ${MUTED_FG};
    }

    &:focus {
      outline: none;
      border-color: ${FG_20};
      box-shadow: 0 0 0 2px ${FG_05};
    }

    @media (max-width: 767px) {
      width: 100%;
      min-width: 200px;
    }
  }

  /* Bulk bar */
  .bulk-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px;
    border: 1px solid #bfdbfe;
    border-radius: 22px;
    background: color-mix(in srgb, #eff6ff 40%, transparent);
  }

  .bulk-count {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 14px;
    font-weight: 500;
    color: ${FG};
  }

  .bulk-select-all-link {
    padding: 0;
    border: none;
    font-size: 13px;
    font-weight: 500;
    color: #2563eb;
    background: transparent;
    cursor: pointer;

    &:hover:not(:disabled) {
      text-decoration: underline;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .bulk-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
  }

  .bulk-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    padding: 0 12px;
    border: 1px solid ${BORDER};
    border-radius: 14px;
    font-size: 13px;
    font-weight: 500;
    color: ${FG};
    background: ${BG};
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover:not(:disabled) {
      background: ${MUTED};
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  /* Table */
  .table-card {
    overflow: hidden;
    border: 1px solid ${BORDER};
    border-radius: 22px;
    background: ${CARD};
    box-shadow: var(--hd-shadow-card);

    /* The filters panel sits beside the table, inside the card. clip, not
       hidden: hidden would stop the panel sticking while the table scrolls. */
    display: flex;
    align-items: flex-start;
    overflow: clip;

    @media (max-width: 900px) {
      flex-direction: column;
      align-items: stretch;
    }

    /* With the panel open the table has 280px less, so it gives up width in
       the places that can spare it, and every column still fits, Actions
       included, without a sideways scroll. Every value here has a number on
       both sides and a transition below, so the columns ease in step with the
       panel opening and closing instead of jumping when the class changes. */
    &.with-filters {
      .contacts-table {
        min-width: 720px;
      }

      .contacts-table th,
      .contacts-table td {
        padding-left: 12px;
        padding-right: 12px;
      }

      .contacts-table th.col-check,
      .contacts-table td.col-check {
        padding-left: 18px;
      }

      .contacts-table td .contact-name-row,
      .contacts-table td .contact-title {
        max-width: 220px;
      }
    }
  }

  .table-main {
    flex: 1;
    min-width: 0;

    .table-head-bar {
      position: relative;
    }

    .table-scroll,
    .table-count {
      transition: opacity 0.2s ease;
    }

    /* Reloading: the rows stay, dimmed, under a thin bar that runs along the
       header, and swap for the new ones when they arrive. */
    &.is-refreshing {
      .table-scroll,
      .table-count {
        opacity: 0.55;
        pointer-events: none;
      }

      .table-head-bar::after {
        content: '';
        position: absolute;
        left: 0;
        bottom: -1px;
        width: 30%;
        height: 2px;
        border-radius: 999px;
        background: var(--hd-gradient-houdiny);
        animation: contactsRefreshBar 1s ease-in-out infinite;
      }
    }
  }

  @keyframes contactsRefreshBar {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(340%);
    }
  }

  .table-head-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: ${TABLE_HEAD_HEIGHT};
    padding: 14px 20px;
    border-bottom: 1px solid ${BORDER};
  }

  .import-banner {
    display: flex;
    align-items: center;
    gap: 16px;
    margin: 0 0 12px;
    padding: 12px 16px;
    border: 1px solid ${BORDER};
    border-radius: 16px;
    background: ${CARD};
    box-shadow: var(--hd-shadow-card);
  }

  .import-banner-kicker {
    flex-shrink: 0;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: ${FG};
  }

  .import-banner-track {
    flex: 1;
    min-width: 80px;
    height: 4px;
    overflow: hidden;
    border-radius: 999px;
    background: ${MUTED};
  }

  .import-banner-track > span {
    display: block;
    height: 100%;
    border-radius: 999px;
    background: var(--hd-gradient-houdiny);
    transition: width 0.25s ease;
  }

  .import-banner-stat {
    flex-shrink: 0;
    font-size: 13px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.03em;
    color: ${FG};
    white-space: nowrap;

    span {
      font-weight: 500;
      color: ${MUTED_FG};
    }
  }

  .import-banner.is-failed .import-banner-track > span {
    background: ${MUTED_FG};
  }

  .import-banner.is-done .import-banner-kicker {
    color: ${MUTED_FG};
  }

  .table-count {
    display: flex;
    align-items: center;
    gap: 10px;

    b {
      font-size: 14px;
      font-weight: 700;
      letter-spacing: -0.025em;
      font-variant-numeric: tabular-nums;
      color: ${FG};
    }

    span {
      font-size: 12px;
      font-weight: 500;
      color: ${MUTED_FG};
    }
  }

  .sort {
    position: relative;
  }

  .sort-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    padding: 0 12px;
    border: 1px solid ${BORDER};
    border-radius: 14px;
    font-family: inherit;
    font-size: 12px;
    font-weight: 600;
    color: ${FG};
    background: ${BG};
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
      background: ${MUTED};
    }

    svg {
      width: 14px;
      height: 14px;
      color: var(--hd-primary);
    }

    .chev {
      width: 12px;
      height: 12px;
      margin-left: 2px;
      color: ${MUTED_FG};
      transition: transform 0.15s ease;
    }

    &.open .chev {
      transform: rotate(180deg);
    }
  }

  .sort-menu {
    ${popover}
    right: 0;
    width: 208px;
    padding: 4px;
    border-radius: 18px;
  }

  .sort-option {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 6px 10px;
    border: none;
    border-radius: 14px;
    font-family: inherit;
    font-size: 12px;
    font-weight: 500;
    text-align: left;
    color: ${MUTED_FG};
    background: transparent;
    cursor: pointer;

    &:hover {
      background: ${MUTED};
    }

    &.selected {
      color: ${FG};
    }

    svg {
      flex-shrink: 0;
      width: 14px;
      height: 14px;
      color: var(--hd-primary);
    }
  }

  .table-scroll {
    overflow-x: auto;
  }

  /**
   * 1200px was the floor for nine columns. Two are parked, so it forced a
   * horizontal scroll the table no longer needs — and every pixel of the surplus
   * went to Contact, because an auto-layout table gives spare width to whichever
   * column has the longest content and a LinkedIn headline is very long indeed.
   * That is what pushed Company, Stage and Score into a strip on the right with
   * Actions cut off past the edge.
   */
  /* 1200px was the floor for nine columns. Two are parked, so it forced a
     horizontal scroll the table no longer needs — and pushed Actions off the
     right edge, where the row menu could not be reached. */
  .contacts-table {
    width: 100%;
    min-width: 980px;
    transition: min-width 0.34s cubic-bezier(0.22, 1, 0.36, 1);
    border-collapse: separate;
    border-spacing: 0;
    font-size: 14px;
    line-height: 20px;
  }

  /**
   * Bound the contact text so the headline cannot set the column width.
   *
   * Both lines already ellipsize, but truncation needs something to truncate
   * against: with no bound, the cell's preferred width is the full headline and
   * the column simply grows to fit it. This caps the contribution, so a long
   * headline ends in an ellipsis rather than at the expense of every other column.
   */
  .contacts-table td .contact-name-row,
  .contacts-table td .contact-title {
    max-width: 360px;
    transition: max-width 0.34s cubic-bezier(0.22, 1, 0.36, 1);
  }

  /* Two-word headings and "No touches" were wrapping to two lines in columns
     that had been squeezed; neither should ever break. */
  .contacts-table th {
    white-space: nowrap;
  }

  .contacts-table thead tr {
    text-align: left;
    background: ${CARD};
  }

  .contacts-table th {
    padding: 12px 16px;
    transition: padding 0.34s cubic-bezier(0.22, 1, 0.36, 1);
    border-bottom: 1px solid ${BORDER};
    font-size: 10px;
    font-weight: 700;
    line-height: 16px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${MUTED_FG};
  }

  .contacts-table th.col-check {
    width: 40px;
    padding-left: 20px;
  }

  /**
   * Left, like every other column.
   *
   * Right-alignment was right when the heading was the single word "Score" and
   * the cell held a compact number — the label and the value lined up. The
   * heading is now "AI Score" plus a Coming soon pill, so the right edge is much
   * further over: the value ended up under the pill, adrift from the words it
   * belongs to, and the only column in the table not aligned with its own header.
   */
  /**
   * Left, like every other column.
   *
   * Right-alignment suited the single word "Score" above a compact number — label
   * and value lined up on the same edge. The heading is now "AI Score" plus a
   * Coming soon pill, which pushes that edge much further over: the value ends up
   * under the pill rather than under the words it belongs to, and it is the only
   * column in the table not aligned with its own header. The skeleton beneath
   * already says left, so this also stops the placeholder jumping when data lands.
   */
  .contacts-table th.col-score,
  .contacts-table td.col-score {
    text-align: left;
  }

  /* Nothing scores a lead yet, so the column is labelled rather than removed —
     it is a real column with no source behind it. */
  .contacts-table th .th-soon {
    display: inline-block;
    margin-left: 8px;
    padding: 2px 7px;
    border-radius: 999px;
    font-size: 8.5px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    white-space: nowrap;
    vertical-align: middle;
    color: var(--hd-muted-foreground);
    background: var(--hd-muted);
  }

  .contacts-table th.col-actions,
  .contacts-table td.col-actions {
    padding-right: 20px;
    text-align: right;
  }

  .contacts-table tbody tr {
    transition: background 0.2s ease;

    &:hover {
      background: ${MUTED_30};

      .row-actions {
        opacity: 1;
      }

      .contact-avatar-img,
      .contact-avatar {
        transform: scale(1.05);
      }
    }

    &:not(:last-child) td {
      border-bottom: 1px solid color-mix(in oklab, ${BORDER} 50%, transparent);
    }
  }

  .contacts-table td {
    padding: 14px 16px;
    vertical-align: middle;
    font-size: 14px;
    line-height: 20px;
    transition: padding 0.34s cubic-bezier(0.22, 1, 0.36, 1);
  }

  .contacts-table td.col-check {
    padding-left: 20px;
  }

  .row-checkbox {
    width: 14px;
    height: 14px;
    border-radius: 4px;
    accent-color: var(--hd-primary);
    cursor: pointer;
  }

  .empty-row {
    padding: 64px 24px;
    text-align: center;
    font-size: 14px;
    color: ${MUTED_FG};
  }
`;

export const StyledKpi = styled.div`
  padding: 16px;
  border: 1px solid ${({ $accent }) => ($accent ? '#bfdbfe' : BORDER)};
  border-radius: 22px;
  background: ${CARD};
  box-shadow: var(--hd-shadow-card);

  .kpi-label {
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: ${MUTED_FG};
  }

  .kpi-value {
    margin-top: 4px;
    font-size: 24px;
    font-weight: 600;
    letter-spacing: -0.025em;
    color: ${FG};

    ${({ $accent }) =>
      $accent &&
      css`
        color: #000;
        -webkit-text-fill-color: #000;
      `}
  }

  .kpi-sub {
    margin-top: 2px;
    font-size: 11px;
    color: ${MUTED_FG};
  }
`;

export const StyledContactsTabs = styled.div`
  display: inline-flex;
  height: 36px;
  align-items: center;
  padding: 2px;
  border: 1px solid ${BORDER};
  border-radius: 14px;
  background: ${CARD};
  box-shadow: var(--hd-shadow-card);

  a,
  .tab-link {
    display: inline-flex;
    height: 100%;
    align-items: center;
    padding: 0 12px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    text-decoration: none;
    color: ${MUTED_FG};
    transition: color 0.15s ease, background 0.15s ease;

    &:hover:not(.disabled) {
      color: ${FG};
    }

    &.active {
      color: ${FG};
      background: ${MUTED};
    }

    &.disabled {
      opacity: 0.45;
      cursor: not-allowed;
      user-select: none;
    }
  }
`;

/* version3: h-10 w-10 rounded-xl, bold initials, a faint ring. */
export const StyledContactAvatar = styled.div`
  display: grid;
  flex-shrink: 0;
  place-items: center;
  width: 40px;
  height: 40px;
  border-radius: 18px;
  font-size: 12px;
  font-weight: 700;
  color: ${({ $text }) => $text};
  background: ${({ $bg }) => $bg};
  box-shadow: 0 0 0 1px color-mix(in oklab, ${BORDER} 50%, transparent);
  ${({ $text }) => darkPastel($text)}
  transition: transform 0.2s ease;
`;

export const StyledSourceBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border: 1px solid ${({ $border }) => $border};
  border-radius: 999px;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.2;
  color: ${({ $text }) => $text};
  background: ${({ $bg }) => $bg};
  white-space: nowrap;
  ${({ $text }) => darkPastel($text)}

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ $dot }) => $dot};
  }
`;

/* version3: rounded-lg border px-2 py-0.5, 10px bold uppercase. */
export const StyledStageBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border: 1px solid ${({ $border }) => $border || BORDER};
  border-radius: 14px;
  font-size: 10px;
  font-weight: 700;
  line-height: 12px;
  letter-spacing: -0.025em;
  text-transform: uppercase;
  white-space: nowrap;
  color: ${({ $text }) => $text};
  background: ${({ $bg }) => $bg};
  ${({ $text }) => darkPastel($text)}
`;

/* version3's ScoreBadge: solid for high intent, outlined for the rest. */
export const StyledScoreBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  padding: 4px 8px;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);

  ${({ $intent }) => {
    if ($intent === 'high') {
      return css`
        color: ${BG};
        background: ${FG};
      `;
    }
    if ($intent === 'medium') {
      return css`
        border: 1px solid ${BORDER};
        color: ${FG};
        background: ${MUTED};
      `;
    }
    return css`
      border: 1px solid ${BORDER};
      color: ${MUTED_FG};
      background: ${BG};
    `;
  }}
`;

export const StyledRowActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  opacity: 0;
  transition: opacity 0.2s ease;

  &:focus-within {
    opacity: 1;
  }
`;

/* version3's IconBtn: h-8 w-8 rounded-lg border bg-background shadow-sm. */
export const StyledIconBtn = styled.button`
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid ${BORDER};
  border-radius: 14px;
  color: ${MUTED_FG};
  background: ${BG};
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
  cursor: pointer;
  transition: all 0.15s ease;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    transform: scale(1.05);
    color: var(--hd-primary);
    border-color: color-mix(in oklab, var(--hd-primary) 20%, transparent);
  }

  &[aria-disabled='true'] {
    cursor: default;
  }
`;

export const StyledAgentLink = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  color: ${FG};

  .agent-mini-avatar {
    display: grid;
    place-items: center;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    font-size: 7px;
    font-weight: 700;
    color: #fff;
    background: var(--hd-gradient-houdiny);
  }
`;

export const StyledCampaignLink = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${MUTED_FG};
`;

export const StyledContactCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;

  .contact-name-row {
    display: flex;
    align-items: center;
    gap: 6px;
    overflow: hidden;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    color: ${FG};
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .verified-icon {
    flex-shrink: 0;
    color: #10b981;
  }

  .contact-title {
    overflow: hidden;
    font-size: 12px;
    line-height: 16px;
    color: ${MUTED_FG};
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .contact-avatar-img {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 18px;
    object-fit: cover;
    box-shadow: 0 0 0 1px color-mix(in oklab, ${BORDER} 50%, transparent);
    transition: transform 0.2s ease;
  }

  .contact-name-link {
    padding: 0;
    border: none;
    font: inherit;
    font-weight: 600;
    color: ${FG};
    background: transparent;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }

    /* Lead preview is off, so the name is not a link — drop the affordances. */
    &.is-static {
      cursor: default;

      &:hover {
        text-decoration: none;
      }
    }
  }
`;

export const StyledContactsTableSkeleton = styled.div`
  .skeleton-row td {
    padding: 14px 16px;
    vertical-align: middle;
    border-bottom: 1px solid ${BORDER};
  }

  .skeleton-contact {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .skeleton-contact-lines {
    flex: 1;
    min-width: 0;
  }

  .skeleton-actions {
    display: flex;
    justify-content: flex-end;
    gap: 6px;
  }

  /* Matches the real table above, so the placeholder does not sit somewhere the
     value will not. */
  .col-score {
    text-align: left;
  }
`;

export const StyledTablePagination = styled.div`
  padding: 12px 20px;
  border-top: 1px solid ${BORDER};
`;

export const StyledCompanyCell = styled.div`
  display: flex;
  flex-direction: column;

  .company-name {
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: ${FG};
  }

  .company-meta {
    font-size: 12px;
    line-height: 16px;
    color: ${MUTED_FG};
  }
`;

export const StyledLastTouch = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  white-space: nowrap;
  color: ${({ $muted }) => ($muted ? MUTED_FG : FG)};

  svg {
    flex-shrink: 0;
    width: 14px;
    height: 14px;
  }

  .email-icon {
    color: ${MUTED_FG};
  }

  .linkedin-icon {
    color: #0a66c2;
  }
`;

/**
 * The "More" filters panel, down the left of the table card. It shares the
 * card's border and background, so it reads as part of the table rather than
 * something laid over it, and the accordion of the shared Filters list is
 * restyled here to match: compact rows, the brand colour on what is open.
 */
export const StyledContactsFiltersPanel = styled.aside`
  /* Opens by growing from nothing to its width, so the table glides aside
     rather than jumping; the contents slide in from the left as it does. */
  /* Runs the full height of the card beside the table, so it never stops
     short of the rows; the contents stick to the top while the table scrolls.
     clip, not hidden: hidden would make this a scroll box and stop the
     contents sticking. */
  flex: 0 0 auto;
  align-self: stretch;
  width: 0;
  overflow: clip;
  border-right: 1px solid transparent;
  background: linear-gradient(
    180deg,
    color-mix(in srgb, var(--hd-muted) 34%, ${CARD}) 0%,
    color-mix(in srgb, var(--hd-muted) 14%, ${CARD}) 100%
  );
  transition:
    width 0.34s cubic-bezier(0.22, 1, 0.36, 1),
    border-color 0.2s ease;

  &.shown {
    width: 280px;
    border-color: ${BORDER};
  }

  /* As tall as the screen allows (never taller than the card), and stuck to
     the top while the table scrolls: whatever part of the card is in view, the
     panel beside it is filled rather than a short list over an empty column. */
  .panel-inner {
    position: sticky;
    top: 80px;
    display: flex;
    flex-direction: column;
    width: 280px;
    height: calc(100vh - 96px);
    max-height: 100%;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  /* An animation, not a transition on transform: once it ends the panel has
     no transform left, and a transform would make the filters' dropdowns
     (fixed-position menus) open offset and get clipped at the panel's edge. */
  &.shown .panel-inner {
    opacity: 1;
    animation: contactsFiltersContentIn 0.4s cubic-bezier(0.22, 1, 0.36, 1);
  }

  @keyframes contactsFiltersContentIn {
    from {
      opacity: 0;
      transform: translateX(-24px);
    }
    to {
      opacity: 1;
      transform: none;
    }
  }

  /* The same height as the table's own header bar, so the two lines meet. */
  .panel-head {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    height: ${TABLE_HEAD_HEIGHT};
    padding: 0 12px 0 16px;
    border-bottom: 1px solid ${BORDER};
  }

  .panel-title {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  .panel-name {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    line-height: 18px;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: ${FG};
  }

  .panel-icon {
    display: grid;
    flex-shrink: 0;
    place-items: center;
    width: 30px;
    height: 30px;
    border-radius: 10px;
    color: var(--hd-primary);
    background: color-mix(in srgb, var(--hd-primary) 10%, transparent);

    svg {
      width: 14px;
      height: 14px;
    }
  }

  .panel-count {
    display: inline-grid;
    place-items: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: #fff;
    background: var(--hd-gradient-houdiny);
  }

  .panel-actions {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .panel-clear {
    height: 28px;
    padding: 0 10px;
    border: none;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    color: var(--hd-primary);
    background: transparent;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
      background: color-mix(in srgb, var(--hd-primary) 8%, transparent);
    }
  }

  .panel-close {
    display: grid;
    place-items: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    border-radius: 999px;
    color: ${MUTED_FG};
    background: transparent;
    cursor: pointer;
    transition: color 0.15s ease, background 0.15s ease;

    svg {
      width: 16px;
      height: 16px;
    }

    &:hover {
      color: ${FG};
      background: ${MUTED_50};
    }
  }

  .panel-label {
    margin: 4px 0 6px;
    padding: 0 12px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${MUTED_FG};
  }

  .panel-body {
    flex: 1;
    min-height: 0;
    padding: 10px 10px 16px;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 999px;
      background: ${MUTED_50};
    }
  }

  /* The shared Filters accordion, compacted for the panel. */
  .panel-body > ul > li {
    margin-bottom: 4px;
    border: 1px solid transparent;
    border-radius: 12px;
    transition:
      background 0.15s ease,
      border-color 0.15s ease,
      box-shadow 0.15s ease;

    &:hover:not(.active) {
      border-color: ${BORDER};
      background: ${CARD};
    }

    .opener {
      padding: 11px 12px;
      border-radius: 12px;
      font-size: 13px;
      line-height: 18px;
      font-weight: 500;
      color: ${FG};
      transition: color 0.15s ease;

      .icon {
        font-size: 16px;
        color: ${MUTED_FG};
        transition: transform 0.2s ease, color 0.15s ease;
      }
    }

    .slide {
      border-top: none;
      background: transparent;
    }

    .list {
      padding: 4px 12px 12px;
    }

    input[type='radio'],
    input[type='checkbox'] {
      accent-color: var(--hd-primary);
    }

    &.active {
      background: ${CARD};
      box-shadow: 0 0 0 1px ${BORDER}, var(--hd-shadow-card);

      .opener {
        color: var(--hd-primary);

        .icon {
          color: var(--hd-primary);
        }
      }
    }
  }

  /* Narrow screens: the panel sits above the table, full width. */
  @media (max-width: 900px) {
    position: static;
    width: 100%;
    max-height: 0;
    border-right: none;
    border-radius: 0;
    transition:
      max-height 0.34s cubic-bezier(0.22, 1, 0.36, 1),
      border-color 0.2s ease;

    &.shown {
      width: 100%;
      max-height: 420px;
    }

    .panel-inner {
      position: static;
      width: 100%;
      max-height: 420px;
    }
  }
`;

export const StyledContactsError = styled.div`
  padding: 12px 16px;
  border: 1px solid #fecaca;
  border-radius: 12px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 14px;
`;

export const StyledLockedField = styled.span`
  filter: ${({ $locked }) => ($locked ? 'blur(4px)' : 'none')};
  user-select: ${({ $locked }) => ($locked ? 'none' : 'auto')};
  cursor: ${({ $locked }) => ($locked ? 'help' : 'inherit')};
  color: ${({ $enriching }) => ($enriching ? '#075985' : 'inherit')};
  font-size: ${({ $enriching }) => ($enriching ? '12px' : 'inherit')};
`;

export const StyledRowMenu = styled.div`
  position: relative;

  .row-menu-panel {
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    z-index: 20;
    min-width: 180px;
    padding: 4px;
    border: 1px solid ${BORDER};
    border-radius: 18px;
    background: ${CARD};
    box-shadow: var(--hd-shadow-elevated);
  }

  .row-menu-item {
    display: flex;
    width: 100%;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border: none;
    border-radius: 14px;
    font-family: inherit;
    font-size: 13px;
    color: ${FG};
    background: transparent;
    cursor: pointer;
    text-align: left;

    &:hover {
      background: ${MUTED_30};
    }
  }
`;

export const StyledAgentCampaignCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 12px;

  .agent-line {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-weight: 500;
    color: ${FG};
  }

  .campaign-line {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: ${MUTED_FG};
  }

  .muted {
    color: ${MUTED_FG};
  }
`;
